import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { colorPaletteGenerator, hexToHsl } from "bevi-color-palette";
import { StyleService } from "../services/StyleService";
import { StyleModel } from "../models/StyleModel";
import ComputedColors from "../utils/ComputedColors";

interface ContextProps {
    style: StyleModel | undefined,
    isLoading: boolean,
    error: string,
    handlePalette: (color: string) => void,
    theme: any,
    hslColor: string,
    colors: string[],
    urlDomus: string,
}

export const StyleContext = createContext<ContextProps>({} as ContextProps);

interface StyleProviderProps {
    children: ReactNode,
}

export const StyleProvider = ({ children }: StyleProviderProps) => {
    const [style, setStyle] = useState<StyleModel>({
        'id': 1,
        'name': 'Bevi',
        'corporateName': 'Bevicred Informações Cadastrais Ltda',
        'colorPrimary': '#2c2a4e',
        'colorSecondary': '#25cbdb',
        'createdAt': '',
        'updatedAt': '',
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [theme, setTheme] = useState<any>();
    const [hslColor, setHslColor] = useState<any>();
    const [colors, setColors] = useState<string[]>([]);
    const [urlDomus, setUrlDomus] = useState<string>('');

    const handlePalette = (color: string) => {
        const _hslColor = hexToHsl(color);
        setHslColor(_hslColor);
        setTheme(colorPaletteGenerator(_hslColor));
    }

    const service = new StyleService();

    useEffect(() => {
        if (!!style) {
            const root = document.documentElement;

            if (style?.colorPrimary) {
                root.style.setProperty('--progress-circle', style?.colorPrimary || '');
                root.style.setProperty('--bs-btn-color', style?.colorPrimary || '');
                root.style.setProperty('--bs-btn-border-color', style?.colorPrimary || '');
                root.style.setProperty('--bs-btn-active-bg', style?.colorPrimary || '');

                const _hslColor = hexToHsl(style?.colorPrimary);
                const _theme = colorPaletteGenerator(_hslColor);
                if (_theme) {
                    root.style.setProperty('--primary', style?.colorPrimary);
                    root.style.setProperty('--primary-10', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l10 ? _theme?.l10 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-20', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l20 ? _theme?.l20 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-30', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l30 ? _theme?.l30 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-40', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l40 ? _theme?.l40 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-50', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l50 ? _theme?.l50 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-60', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l60 ? _theme?.l60 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-70', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l70 ? _theme?.l70 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-80', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l80 ? _theme?.l80 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-802', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l802 ? _theme?.l802 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-90', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l90 ? _theme?.l90 : _hslColor.l}%)`);
                    root.style.setProperty('--primary-902', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l902 ? _theme?.l902 : _hslColor.l}%)`);
                }
            }

            if (style?.colorSecondary) {
                const _hslColor = hexToHsl(style?.colorSecondary);
                const _theme = colorPaletteGenerator(_hslColor);
                if (_theme) {
                    root.style.setProperty('--secondary', style?.colorSecondary);
                    root.style.setProperty('--secondary-10', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l10 ? _theme?.l10 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-20', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l20 ? _theme?.l20 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-30', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l30 ? _theme?.l30 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-40', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l40 ? _theme?.l40 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-50', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l50 ? _theme?.l50 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-60', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l60 ? _theme?.l60 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-70', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l70 ? _theme?.l70 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-80', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l80 ? _theme?.l80 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-802', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l802 ? _theme?.l802 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-90', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l90 ? _theme?.l90 : _hslColor.l}%)`);
                    root.style.setProperty('--secondary-902', `hsl(${_hslColor.h}, ${_hslColor.s}%, ${_theme?.l902 ? _theme?.l902 : _hslColor.l}%)`);
                }
            }

            const _colors = ComputedColors();
            setColors(Array.isArray(_colors) ? _colors : []);

            const _url = (process.env.REACT_APP_ENV !== 'production') ? process.env.REACT_APP_DOMUS_DEV : `sistema.bevicred.com.br`;
            const originAccess = localStorage.getItem('@beviAjudaGui:origin');

            setUrlDomus(originAccess || (_url || ''));
        }
    }, [style]);

    return (
        <StyleContext.Provider value={{
            style,
            isLoading,
            error,
            handlePalette,
            urlDomus,
            theme,
            hslColor,
            colors
        }}>
            {children}
        </StyleContext.Provider>
    );
};

export const useStyle = () => useContext(StyleContext);